import React, { useEffect, useReducer } from 'react';
import {
  Alert,
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { getStockBaskets } from '../api';
import StockLogo from '../components/StockLogo';
import { useColorModeValue } from '../components/ui/color-mode';
import { isRecoverableAuthError, getLoginRedirectOptions } from '../utils/auth0Errors';

function BasketCard({ basket, bg, borderColor, mutedText }) {
  return (
    <Box
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="2xl"
      bg={bg}
      p={{ base: 5, md: 6 }}
      boxShadow="sm"
    >
      <Stack gap={5} h="100%">
        <Box>
          <Heading as="h3" size="md">
            {basket.title}
          </Heading>
          <Text mt={2} color={mutedText}>
            {basket.description || 'No description yet.'}
          </Text>
        </Box>

        <Flex wrap="wrap" gap={2}>
          {basket.tickers.map((ticker) => (
            <Box
              key={ticker}
              px={2.5}
              py={1}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="full"
              fontSize="xs"
              fontWeight="semibold"
              display="inline-flex"
              alignItems="center"
              gap={2}
            >
              <StockLogo ticker={ticker} size={24} />
              {ticker}
            </Box>
          ))}
        </Flex>

        <Text fontSize="sm" color={mutedText}>
          {basket.tickers.length} {basket.tickers.length === 1 ? 'company' : 'companies'}
        </Text>

        <Button as={RouterLink} to={`/stock-baskets/${basket.id}`} alignSelf="start" mt="auto">
          Open basket
        </Button>
      </Stack>
    </Box>
  );
}

function BasketSection({ title, description, baskets, emptyState, ...cardColors }) {
  return (
    <Stack gap={4}>
      <Box>
        <Heading as="h2" size="lg">
          {title}
        </Heading>
        <Text mt={2} color={cardColors.mutedText}>
          {description}
        </Text>
      </Box>

      {baskets.length > 0 ? (
        <SimpleGrid columns={{ base: 1, lg: 2 }} gap={5}>
          {baskets.map((basket) => (
            <BasketCard key={basket.id} basket={basket} {...cardColors} />
          ))}
        </SimpleGrid>
      ) : (
        <Box
          borderWidth="1px"
          borderColor={cardColors.borderColor}
          borderRadius="2xl"
          bg={cardColors.bg}
          p={{ base: 5, md: 6 }}
          boxShadow="sm"
        >
          <Text color={cardColors.mutedText}>{emptyState}</Text>
        </Box>
      )}
    </Stack>
  );
}

const initialBasketsState = {
  sampleBaskets: [],
  userBaskets: [],
  loading: true,
  error: '',
};

function basketsReducer(state, action) {
  switch (action.type) {
    case 'load_start':
      return { ...state, loading: true, error: '' };
    case 'load_success':
      return {
        ...state,
        loading: false,
        sampleBaskets: action.sampleBaskets,
        userBaskets: action.userBaskets,
        error: '',
      };
    case 'load_error':
      return {
        ...state,
        loading: false,
        error: action.error,
      };
    default:
      return state;
  }
}

function StockBaskets() {
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const loginWithRedirectRef = React.useRef(loginWithRedirect);
  loginWithRedirectRef.current = loginWithRedirect;
  const navigate = useNavigate();
  const [{ sampleBaskets, userBaskets, loading: loadingBaskets, error }, dispatch] = useReducer(
    basketsReducer,
    initialBasketsState
  );

  const pageBgStart = useColorModeValue('gray.50', 'gray.950');
  const pageBgEnd = useColorModeValue('teal.100', 'gray.900');
  const panelBg = useColorModeValue('white', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    let cancelled = false;

    async function loadBaskets() {
      dispatch({ type: 'load_start' });

      try {
        let token;
        if (isAuthenticated) {
          if (cancelled) {
            return;
          }
          token = await getAccessTokenSilently();
          if (cancelled) {
            return;
          }
        }

        if (cancelled) {
          return;
        }
        const data = await getStockBaskets(token);
        if (cancelled) {
          return;
        }

        dispatch({
          type: 'load_success',
          sampleBaskets: data.sample_baskets || [],
          userBaskets: data.user_baskets || [],
        });
      } catch (err) {
        if (cancelled) {
          return;
        }
        if (isRecoverableAuthError(err)) {
          await loginWithRedirectRef.current(getLoginRedirectOptions());
          return;
        }
        dispatch({
          type: 'load_error',
          error: err.message || 'Failed to load stock baskets',
        });
      }
    }

    if (!isLoading) {
      void loadBaskets();
    }

    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, isAuthenticated, isLoading]);

  const handleCreateBasket = async () => {
    if (isAuthenticated) {
      navigate('/stock-baskets/new');
      return;
    }

    await loginWithRedirect({
      appState: { returnTo: '/stock-baskets/new' },
    });
  };

  return (
    <Box
      minH="calc(100vh - var(--nav-h))"
      bgGradient="to-b"
      gradientFrom={pageBgStart}
      gradientTo={pageBgEnd}
    >
      <Box maxW="1200px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 8, md: 10 }}>
        <Stack gap={10}>
          <Flex
            justify="space-between"
            align={{ base: 'start', md: 'end' }}
            direction={{ base: 'column', md: 'row' }}
            gap={5}
          >
            <Box maxW="760px">
              <Text
                fontSize="sm"
                fontWeight="semibold"
                textTransform="uppercase"
                letterSpacing="0.08em"
                color={mutedText}
              >
                Stock Baskets
              </Text>
              <Heading as="h1" mt={2} fontSize={{ base: '3xl', md: '5xl' }} lineHeight="1.05">
                View a group of stocks as if they were a single business
              </Heading>
              <Text mt={4} fontSize={{ base: 'md', md: 'lg' }} color={mutedText}>
                Browse sample baskets or create your own.
              </Text>
            </Box>

            <Button colorPalette="teal" size="lg" onClick={() => void handleCreateBasket()}>
              Create basket
            </Button>
          </Flex>

          {error ? (
            <Alert.Root status="error">
              <Alert.Indicator />
              <Alert.Content>{error}</Alert.Content>
            </Alert.Root>
          ) : null}

          {loadingBaskets ? (
            <Flex justify="center" py={12}>
              <Spinner size="lg" color="teal.500" />
            </Flex>
          ) : (
            <>
              <BasketSection
                title={isAuthenticated ? 'Your baskets' : 'Your baskets'}
                description={
                  isAuthenticated
                    ? 'Private baskets you create are saved to your account and only visible to you.'
                    : 'Sign in to create private baskets and keep them attached to your account.'
                }
                baskets={userBaskets}
                emptyState={
                  isAuthenticated
                    ? 'You have not created any baskets yet.'
                    : 'No user baskets are shown while signed out.'
                }
                bg={panelBg}
                borderColor={borderColor}
                mutedText={mutedText}
              />

              <BasketSection
                title="Sample baskets"
                description="Public sample baskets give users something to explore immediately."
                baskets={sampleBaskets}
                emptyState="No sample baskets are available right now."
                bg={panelBg}
                borderColor={borderColor}
                mutedText={mutedText}
              />
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

export default StockBaskets;
